<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    :inputs="inputs"
    @save="save"
  >
    <template #layerId.input="data">
      <v-col cols="3">
        <data-input v-model="selectedDataSourceProps.settings.layer_id" v-bind="data" @change="layerIdChanged" />
      </v-col>
    </template>
    <template #attribute_station_id.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSourceProps.settings['attribute_station_id']"
          @change="selectedDataSourcePropsChanged = true"
        />
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'RealtimeStationsSettings',
  components: {
    AdminWrapper,
    DataInput,
  },
  mixins: [prepareAttributesFeatureCardMixin],
  data: () => ({
    selectedDataSourceProps: {
      settings: {
        layer_id: null,
        attribute_station_id: '',
      },
      datasource: '',
    },
    selectedDataSourcePropsChanged: false,
    isFormFetched: false,
  }),
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
  computed: {
    additionalModuleName: get('admin/additionalModules.REALTIME_STATIONS_MEASUREMENTS.verbose_name'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    isModuleEnabled: get('admin/settingsValues@realtime_stations_measurements_module_enabled'),
    dataSourceSettingsName() {
      return 'uke_stations_datasource';
    },
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    layersFiltered() {
      return this.layersArray.filter(
        layer => layer.type === 'features_layer' && layer.layer_scope === 'core' && layer.geometry_type === 'point'
      );
    },
    layersMapped() {
      return this.layersFiltered.map(layer => {
        const { id, name } = layer;
        return { value: id, text: name };
      });
    },
    selectedLayerMetadata() {
      return this.layers?.[this.selectedDataSourceProps.settings.layer_id] || undefined;
    },
    selectedLayerFormSchema() {
      return this.selectedLayerMetadata?.form_schema?.elements || [];
    },
    selectedLayerDataSourceName() {
      return this.selectedLayerMetadata?.data_source_name || '';
    },
    selectedLayerDataSourceMetadata() {
      return this.dataSources?.[this.selectedLayerDataSourceName] || undefined;
    },
    selectedLayerDataSourceAttributesSchema() {
      return this.selectedLayerDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedLayerAttributesArray() {
      return this.$_flattenStructure(
        this.getForm(this.selectedLayerFormSchema, this.selectedLayerDataSourceAttributesSchema, [], true)
      );
    },
    selectedLayerStationIdAttributesArray() {
      return this.selectedLayerAttributesArray.filter(attribute => attribute.dataType.name === 'text');
    },
    selectedLayerMappedTextAttributesArray() {
      return this.selectedLayerStationIdAttributesArray.map(attribute => {
        const { attribute: value, label } = attribute;
        return { value, text: label };
      });
    },
    inputs() {
      return [
        {
          name: 'layerId',
          translationPath: 'admin.realtimeStationsSettings.layerTitle',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.layersMapped,
        },
        {
          name: 'attribute_station_id',
          translationPath: 'admin.realtimeStationsSettings.stationIdAttributeTitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedLayerMappedTextAttributesArray,
          disabled: !this.selectedLayerDataSourceName,
        },
        {
          name: 'realtime_stations_measurements_websocket_address',
          translationPath: 'admin.realtimeStationsSettings.websocketAddress',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'realtime_stations_directions_websocket_address',
          translationPath: 'admin.realtimeStationsSettings.websocketDirectionAddress',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
          },
        },
      ];
    },
    moduleEnabler() {
      return {
        name: 'realtime_stations_measurements_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.init();
      }
    },
  },
  methods: {
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    getDataSources: call('layers/getLayersMetadata'),
    getLayers: call('layers/getLayers'),
    getModulesMapping: call('admin/getModulesMapping'),
    clearColumns() {
      this.selectedDataSourceProps.settings.attribute_station_id = '';
      this.selectedDataSourcePropsChanged = true;
    },
    layerIdChanged(value) {
      this.clearColumns();
      if (!value && value !== 0) return;
      this.selectedDataSourceProps.datasource = this.dataSources?.[this.selectedLayerDataSourceName]?.name || '';
    },
    async init() {
      this.isFormFetched = false;
      await Promise.all([
        this.getModulesMapping(),
        this.getDataSources({ with_attributes_schema: true }),
        this.getLayers(),
      ]);
      if (this.isModuleEnabled) {
        await this.updateSelectedDataSource();
      }
      this.isFormFetched = true;
    },
    async save() {
      if (!this.selectedDataSourcePropsChanged) {
        return;
      }
      const payload = { name: this.dataSourceSettingsName, settings: this.selectedDataSourceProps };
      try {
        await this.editDatasourceSettings(payload);
        this.selectedDataSourcePropsChanged = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch (error) {
        console.log('  error', error);
      }
    },
    async updateSelectedDataSource() {
      try {
        const r = await this.getDatasourceSettings(this.dataSourceSettingsName);
        const { datasource, settings } = r;
        this.selectedDataSourceProps = {
          datasource,
          settings,
        };
        this.selectedDataSourcePropsChanged = false;
      } catch {
        this.selectedDataSourceProps = {
          settings: {
            attribute_station_id: '',
          },
          datasource: '',
        };
        this.selectedDataSourcePropsChanged = false;
      }
    },
  },
};
</script>
